import type { CSSProperties } from "styled-components";

import { OpenAPI } from "simplydo/interfaces";

import SearchParams from "./searchParams";

let isLocalStorageSupported: boolean | undefined;

export const localStorageIsSupported = () => {
  if (isLocalStorageSupported !== undefined) {
    return isLocalStorageSupported;
  }
  try {
    const storage = window.localStorage;
    const key = "test_key_simplydo_local_storage";
    storage.setItem(key, key);
    storage.removeItem(key);

    isLocalStorageSupported = true;
    return true;
  } catch (_e) {
    isLocalStorageSupported = false;
    return false;
  }
};

export const clearLocalStorage = () => {
  if (!localStorageIsSupported()) {
    return;
  }
  window.localStorage.clear();
};

export const getLocalStorageItem = (key: string) => {
  if (!localStorageIsSupported()) {
    return null;
  }
  try {
    return window?.localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
};

export const setLocalStorageItem = (key: string, value: string) => {
  if (!localStorageIsSupported()) {
    return;
  }
  try {
    window?.localStorage.setItem(key, value);
  } catch (_e) {
    return;
  }
};

export const removeLocalStorageItem = (key: string) => {
  if (!localStorageIsSupported()) {
    return;
  }
  try {
    window?.localStorage.removeItem(key);
  } catch (_e) {
    return;
  }
};

export const parseJwt = (token: string) => {
  if (!token) return;
  const base64Url = token.split(".")[1];
  if (!base64Url) {
    return;
  }

  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(""),
  );
  return JSON.parse(jsonPayload);
};

export const pluralise = (
  count: number,
  singular: string,
  plural: string,
  includeNumber = true,
) => {
  return `${includeNumber ? `${count || 0} ` : ""}${count === 1 ? singular : plural}`;
};

export const truncate = (text: string | undefined, length: number) => {
  if (!text) {
    return null;
  }
  if (text.length > length) {
    return `${text.substring(0, length)}...`;
  }
  return text;
};

/**
 * ssx is a utility function to merge multiple style objects into one.
 * @param args - An array of style objects, which can be CSSProperties, or objects with string or number values.
 * It can also handle null or undefined values gracefully.
 * It will only include properties with truthy values in the final style object.
 * @returns A single CSSProperties object that combines all the provided styles, excluding any properties with falsy values.
 */
export const ssx = (
  ...args: (
    | { [key: string]: number | string | undefined }
    | CSSProperties
    | null
    | undefined
  )[]
): CSSProperties => {
  return args.reduce((style, arg) => {
    if (!arg) {
      return style;
    }
    if (typeof arg === "object") {
      const validStyle = Object.entries(arg).reduce<CSSProperties>(
        (acc, [key, value]) => {
          if (value) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );
      return { ...style, ...validStyle };
    }
    return style;
  }, {});
};

export const uuidv4 = () => {
  // http://www.ietf.org/rfc/rfc4122.txt
  const s = [];
  const hexDigits = "0123456789abcdef";
  for (let i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  // @ts-ignore
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-";

  const uuid = s.join("");
  return uuid;
};

export const getOrgCode = () => {
  const params = new SearchParams(window.location.search);
  const code = params.get("code");
  if (code) {
    return code;
  }
  const url = window.location.hostname.toLowerCase();
  if (url.indexOf("app.simplydo.co.uk") > -1) return null;
  if (url.indexOf("simplydo.co.uk") > -1) return url.split(".")[0];
  if (url.indexOf("localhost") > -1) return "simplydo";
  return null;
};

export const getBaseUrl = (
  stage: string,
  organisation: OpenAPI.Schemas["Organisation"] | undefined,
  path = "",
) => {
  path = path ? `/${path.replace(/^\/+/, "")}` : ""; // Ensure path starts with single slash
  if (stage === "dev") {
    return `http://localhost:4000${path}`;
  }
  if (organisation?.customDomain && organisation?.customDomainEnabled) {
    return `https://${organisation.customDomain}${path}`;
  }
  if (organisation?.code) {
    return `https://${organisation.code}.simplydo.co.uk${path}`;
  }
  const orgCode = getOrgCode();
  return `https://${orgCode || "app"}.simplydo.co.uk${path}`;
};
